import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface ApiKey {
  id: string
  name: string
  key: string
  key_hint?: string
  scope: 'send' | 'full'
  type: 'live' | 'test'
  last_used_at: string | null
  created_at: string
}

interface ApiKeyListResponse {
  data: ApiKey[]
}

interface CreateApiKeyRequest {
  name: string
  scope: 'send' | 'full'
  type: 'live' | 'test'
}

interface CreateApiKeyResponse {
  id: string
  key: string
  name: string
  scope: 'send' | 'full'
  type: 'live' | 'test'
}

const QUERY_KEY = ['api-keys'] as const

export function useApiKeys(): ReturnType<typeof useQuery<ApiKeyListResponse>> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get<ApiKeyListResponse>(endpoints.apiKeys.list),
  })
}

export function useCreateApiKey(): ReturnType<
  typeof useMutation<CreateApiKeyResponse, unknown, CreateApiKeyRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateApiKeyRequest) =>
      apiClient.post<CreateApiKeyResponse>(endpoints.apiKeys.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useRevokeApiKey(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.apiKeys.revoke(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export type { ApiKey, CreateApiKeyRequest, CreateApiKeyResponse }
